// applications/_shared/types/upload — the document-upload wire shapes, shared by `dossier` (the
// draft wizard) and `respond` (fulfilling a coordinator's information request): both attach a file
// to an application through the SAME three-call sequence (intent → direct-to-storage → complete).
import { z } from "zod";

/**
 * `POST /applications/{id}/documents/upload-intents/` — the first of three calls in the upload
 * sequence. `upload` is a boto3 presigned-POST descriptor: `fields` must be sent verbatim as form
 * fields, `file` appended LAST (S3 requires that ordering), POSTed directly to `url` — never
 * through `privateApi`, which would attach this app's bearer token and cookies to a different
 * origin for no reason.
 *
 * ⚠️ Response is NOT the full Application DTO — `{document_id, status, application_revision,
 * upload}` only. The caller is responsible for refetching the application once the sequence
 * completes; there is nothing here to replace the cache wholesale WITH.
 */
export const UploadIntentSchema = z.object({
  document_id: z.string(),
  status: z.string(),
  application_revision: z.number().int(),
  upload: z.object({
    url: z.string(),
    fields: z.record(z.string(), z.string()),
  }),
});

export type UploadIntent = z.infer<typeof UploadIntentSchema>;

/** `POST /applications/{id}/documents/{documentId}/complete/` — same narrow shape as the intent
 * response, not the full DTO. */
export const DocumentCompleteSchema = z.object({
  document_id: z.string(),
  status: z.string(),
  application_revision: z.number().int(),
});

export type DocumentComplete = z.infer<typeof DocumentCompleteSchema>;
